
import React, { useState } from 'react';
import { Ad, VendorProfile, Neighborhood } from '../types';
import { supabase } from '../lib/supabase';

interface AdCardProps {
  ad: Ad;
  vendor: VendorProfile | undefined;
  neighborhood: Neighborhood | undefined;
  isVerifiedBadge?: boolean;
}

const AdCard: React.FC<AdCardProps> = ({ ad, vendor, neighborhood, isVerifiedBadge }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleWhatsAppClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); 
    if (!vendor) return;

    if ((window as any).gtag) {
      (window as any).gtag('event', 'whatsapp_click', {
        item_id: ad.id,
        item_name: ad.title,
        vendor_name: vendor.name,
        neighborhood: neighborhood?.name,
        source: 'ad_card'
      });
    }

    try {
      await supabase
        .from('anuncios')
        .update({ clicks_contato: (ad.clicks || 0) + 1 })
        .eq('id', ad.id);
    } catch (error) {
      console.error("Erro ao registrar clique:", error);
    }

    const text = `Olá ${vendor.name}! Vi seu anúncio "${ad.title}" no Compre em Guarulhos e tenho interesse.`;
    window.open(`https://wa.me/${vendor.whatsapp}?text=${encodeURIComponent(text)}`, '_blank');
  };

  return (
    <div 
      className={`bg-white h-full flex flex-col rounded-xl sm:rounded-3xl overflow-hidden shadow-sm border transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 relative cursor-pointer ${isVerifiedBadge ? 'border-brand-orange/40 ring-1 ring-brand-orange/10 shadow-orange-100/50' : 'border-gray-100'}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-40 sm:h-56 overflow-hidden bg-gray-100">
        <img 
          src={ad.images[0]} 
          alt={ad.title} 
          className={`w-full h-full object-cover transition-transform duration-1000 ease-out ${isHovered ? 'scale-110' : 'scale-100'}`}
        />
        
        {/* Indicador de múltiplas fotos */}
        {ad.images.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/40 backdrop-blur-md px-2 py-1 rounded-lg flex items-center gap-1.5 border border-white/20">
             <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20"><path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"/></svg>
             <span className="text-[8px] font-black text-white uppercase tracking-tighter">+{ad.images.length - 1} fotos</span>
          </div>
        )}

        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          <div className="bg-white/95 backdrop-blur-md px-3 py-1 rounded-xl text-[9px] font-black text-brand-navy shadow-xl uppercase tracking-widest border border-gray-100">
            📍 {neighborhood?.name}
          </div>
        </div>
        
        {isVerifiedBadge && (
          <div className="absolute top-3 right-3 bg-brand-orange p-2 rounded-2xl shadow-xl border-2 border-white animate-in zoom-in duration-500">
            <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.64.304 1.24.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>
      
      <div className="p-4 sm:p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[8px] font-black text-brand-orange uppercase tracking-widest">{ad.category_id && 'OFERTA LOCAL'}</span>
        </div>
        
        <h3 className="font-title text-sm sm:text-base font-black text-brand-navy mb-1 line-clamp-2 leading-tight uppercase tracking-tighter italic">{ad.title}</h3>
        
        <div className="flex items-center gap-1.5 mb-4">
          <p className="text-[9px] sm:text-[10px] text-gray-400 font-bold uppercase truncate max-w-[80%]">
            {vendor?.name}
          </p>
        </div>
        
        <div className="mt-auto flex items-end justify-between pt-4 border-t border-gray-50">
          <div className="flex flex-col">
            <span className="text-xs text-gray-300 font-black uppercase leading-none mb-1">Preço Sugerido</span>
            <span className="text-base sm:text-xl font-black text-brand-blue leading-none">
              {ad.price_on_request ? 'CONSULTAR' : ad.price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </span>
          </div>
          
          <button 
            onClick={handleWhatsAppClick}
            className="bg-[#25D366] text-white p-3 rounded-2xl shadow-xl shadow-green-500/20 transition-all hover:scale-110 active:scale-90"
          >
             <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.937 3.659 1.432 5.632 1.433h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdCard;
